import { http } from '@/app/lib/http/client'
import type { InventoryAllocation } from './pos.service'

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface TransferProductItem {
  id?: number
  product_id: number
  product?: {
    id: number
    name: string
    sku?: string
    barcode?: string
    product_type?: { id: number; name: string; requires_lot?: boolean | number } | null
  }
  quantity?: number
  requested_quantity?: number
  approved_quantity?: number
  received_quantity?: number
  status?: string
  response_type?: 'FULL' | 'PARTIAL' | 'REJECTED'
  response_reason?: string
  reason?: string
  allocations?: InventoryAllocation[]
}

export interface TransferRequest {
  id: number
  origin_sucursal_id: number
  destination_sucursal_id: number
  notes: string
  status: string
  created_at: string
  items: TransferProductItem[]
  origin_sucursal?: { id: number; name: string }
  destination_sucursal?: { id: number; name: string }
}

export interface InventoryTransfer {
  id: number
  origin_sucursal_id: number
  destination_sucursal_id: number
  notes: string
  status: string
  created_at: string
  items: TransferProductItem[]
  origin_sucursal?: { id: number; name: string }
  destination_sucursal?: { id: number; name: string }
}

export interface TransferListItem {
  id: string
  date: string
  fromBranch: string
  toBranch: string
  products: { name: string; quantity: number }[]
  status: 'pending' | 'completed'
  _originalData: TransferRequest | InventoryTransfer
  _type: 'request' | 'transfer'
}

// Payloads
export interface CreateTransferRequestPayload {
  origin_sucursal_id: number
  destination_sucursal_id: number
  notes?: string
  items: { product_id: number; requested_quantity: number }[]
}

export interface ShipTransferRequestPayload {
  notes?: string
  items: { 
    product_id: number; 
    response_type: 'FULL' | 'PARTIAL' | 'REJECTED'; 
    approved_quantity?: number; 
    response_reason?: string
    allocations?: InventoryAllocation[]
  }[]
}

export interface DirectTransferPayload {
  origin_sucursal_id: number
  destination_sucursal_id: number
  notes?: string
  items: { product_id: number; quantity: number; allocations?: InventoryAllocation[] }[]
}

export interface ReceiveTransferPayload {
  notes?: string
  items: { product_id: number; status: 'RECEIVED' | 'NOT_RECEIVED'; reason?: string }[]
}

export interface CancelPayload {
  reason: string
}

export type TransferExportContext = 'send' | 'request' | 'receive' | 'history'
export type TransferExportFormat = 'pdf' | 'xlsx' | 'excel'

export interface ExportTransferDetailsPayload {
  context: TransferExportContext
  format: TransferExportFormat
  sucursal_id?: number
  transfer_request_id?: number
  transfer_request_ids?: number[]
  inventory_transfer_id?: number
  inventory_transfer_ids?: number[]
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const transferService = {
  // === SOLICITAR (Requesting) ===
  
  // Ver sucursales disponibles (o usar el catalogo global)
  async getMySucursals() {
    const response = await http.get('/auth/my-sucursals')
    return response.data
  },

  // Crear solicitud
  async createTransferRequest(payload: CreateTransferRequestPayload) {
    const response = await http.post('/transfer-requests', payload)
    return response.data
  },

  // Ver solicitudes creadas (Pendientes que yo solicité)
  async getMyPendingRequests(mySucursalId: number) {
    const response = await http.get(`/transfer-requests`, {
      params: { destination_sucursal_id: mySucursalId, status: 'PENDING', all: true }
    })
    return response.data
  },

  // Ver detalle de solicitud
  async getTransferRequest(id: number) {
    const response = await http.get(`/transfer-requests/${id}`)
    return response.data
  },

  // Cancelar solicitud
  async cancelTransferRequest(id: number, payload: CancelPayload) {
    const response = await http.post(`/transfer-requests/${id}/cancel`, payload)
    return response.data
  },

  // === ENVIAR (Sending) ===

  // Listar solicitudes que me hicieron (Pendientes de Envío)
  async getRequestsToShip(mySucursalId: number) {
    const response = await http.get(`/transfer-requests`, {
      params: { origin_sucursal_id: mySucursalId, status: 'PENDING', all: true }
    })
    return response.data
  },

  // Confirmar envío (Completo o Parcial)
  async shipTransferRequest(id: number, payload: ShipTransferRequestPayload) {
    const response = await http.post(`/transfer-requests/${id}/ship`, payload)
    return response.data
  },

  // Rechazar solicitud completa
  async rejectTransferRequest(id: number, payload: CancelPayload) {
    const response = await http.post(`/transfer-requests/${id}/reject`, payload)
    return response.data
  },

  // Envío Directo A Otra Sucursal
  async createDirectTransfer(payload: DirectTransferPayload) {
    const response = await http.post('/inventory-transfers', payload)
    return response.data
  },

  // Listar transferencias enviadas
  async getSentTransfers(mySucursalId: number) {
    const response = await http.get(`/inventory-transfers`, {
      params: { origin_sucursal_id: mySucursalId, status: 'PENDING_RECEPTION', all: true }
    })
    return response.data
  },

  // Cancelar transferencia pendiente de recepción
  async cancelSentTransfer(id: number, payload: CancelPayload) {
    const response = await http.post(`/inventory-transfers/${id}/cancel`, payload)
    return response.data
  },

  // === RECIBIR (Receiving) ===

  // Listar pendientes por recibir
  async getPendingReceptions(mySucursalId: number) {
    const response = await http.get(`/inventory-transfers/pending-reception`, {
      params: { sucursal_id: mySucursalId, all: true }
    })
    return response.data
  },

  // Ver detalle de la transferencia de inventario
  async getInventoryTransfer(id: number) {
    const response = await http.get(`/inventory-transfers/${id}`)
    return response.data
  },

  // Confirmar recepción
  async receiveTransfer(id: number, payload: ReceiveTransferPayload) {
    const response = await http.post(`/inventory-transfers/${id}/receive`, payload)
    return response.data
  },

  async exportTransferDetails(payload: ExportTransferDetailsPayload) {
    return http.post('/reports/transfer-details/export', payload, {
      responseType: 'blob',
    })
  }
}
